import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Toggle from './Toggle';
import { FilterConditionRow, FilterCondition } from './FilterConditionRow';
import SearchViewSelect from './SearchViewSelect';
import { useCompatibleSearchViews } from '../hooks/useSearchViews';
import { useFieldDefinitions } from '../contexts/FieldDefinitionsContext';
import searchService from '../services/search.service';
import {
  buildFilterJson,
  parseFilterJson,
} from '../pages/order-management/components/CustomFilterDialog';

/* Body of the header's Custom Filter modal.
 *
 * The same condition builder the LC/Dispatch dashboard cards and the quotes Custom
 * Filter use (FilterConditionRow), pointed at the orders field registry. Applying
 * emits a JSON FilterCondition[] which the backend feeds to DynamicQueryBuilder.
 * Conditions are AND-ed; combine values within one field via comma / multi-select.
 *
 * Saved filters live in fastcms_db.quote_custom_filters under scope='orders' — the
 * table is scope-generic despite its quote-flavored name.
 *
 * The modal chrome (drag, close) belongs to DraggableModal; this owns the inputs. */

const EMPTY: FilterCondition = { id: 0, field: '', compare: '', values: '' };

interface SavedFilter {
  recno: number;
  name: string;
  filter: string;
}

interface Props {
  /** Fires with the JSON FilterCondition[] — never with an empty filter. */
  onApply: (filterJson: string, includeDeleted: boolean, viewId: string) => void;
  onCancel: () => void;
  /** Active filter JSON, prefilled so the user can edit and re-run it. */
  initialFilter?: string;
  initialIncludeDeleted?: boolean;
  /**
   * Active View id. Reopening this modal from a results view (the "Edit Filter"
   * button) must carry it through, or refining a filter would silently drop the
   * layout the user is looking at.
   */
  initialViewId?: string;
}

/** Canonical JSON so a saved filter round-tripped through the builder compares
 *  equal to a freshly-built one (drives the "edited / unsaved" marker). */
function canon(raw?: string): string {
  return buildFilterJson(parseFilterJson(raw));
}

const CustomOrderFilterForm: React.FC<Props> = ({
  onApply,
  onCancel,
  initialFilter,
  initialIncludeDeleted = false,
  initialViewId = '',
}) => {
  const { fieldDefinitions, loading: loadingFields } = useFieldDefinitions();
  const { views, myViews } = useCompatibleSearchViews();
  // Hide fields the registry marks unfilterable — the backend ignores them anyway,
  // so offering them would just produce a condition that silently does nothing.
  const filterableFieldDefs = useMemo(
    () => fieldDefinitions.filter((d) => d.is_filterable !== false),
    [fieldDefinitions],
  );

  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    const parsed = parseFilterJson(initialFilter);
    return parsed.length ? parsed : [{ ...EMPTY }];
  });
  const [idCounter, setIdCounter] = useState(() => parseFilterJson(initialFilter).length || 1);
  const [includeDeleted, setIncludeDeleted] = useState(initialIncludeDeleted);
  const [viewId, setViewId] = useState(initialViewId);
  const [saved, setSaved] = useState<SavedFilter[]>([]);
  const [saveName, setSaveName] = useState('');
  // The saved filter currently loaded, kept across edits so we can show an
  // "edited / unsaved" marker and offer "Update <name>".
  const [loaded, setLoaded] = useState<{ name: string; filter: string } | null>(null);

  const refreshSaved = useCallback(
    () => searchService.listOrderCustomFilters().then(setSaved).catch(() => setSaved([])),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    searchService
      .listOrderCustomFilters()
      .then((list) => {
        if (cancelled) return;
        setSaved(list);
        // Surface the name when the active filter matches a saved one.
        const target = canon(initialFilter);
        const match = target ? list.find((f) => canon(f.filter) === target) : undefined;
        if (match) setLoaded({ name: match.name, filter: canon(match.filter) });
      })
      .catch(() => setSaved([]));
    return () => { cancelled = true; };
  }, [initialFilter]);

  // Which row's field picker gets the cursor when it mounts: the first row on open,
  // then whichever row Tab-off-the-last-row just appended. Rows keyed by id only
  // mount once, so an existing row never steals focus back on re-render.
  const [focusId, setFocusId] = useState<number>(() => parseFilterJson(initialFilter)[0]?.id ?? 0);

  const addCondition = useCallback((focusNew = false) => {
    setConditions((prev) => [...prev, { id: idCounter, field: '', compare: '', values: '' }]);
    if (focusNew) setFocusId(idCounter);
    setIdCounter((n) => n + 1);
  }, [idCounter]);

  const removeCondition = useCallback((id: number) => {
    setConditions((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : [{ ...EMPTY }]));
  }, []);

  const updateCondition = useCallback((id: number, field: keyof FilterCondition, value: string) => {
    setConditions((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        // Operators and the value input are field-typed, so changing the field
        // invalidates both.
        if (field === 'field') return { ...c, field: value, compare: '', values: '' };
        return { ...c, [field]: value };
      }),
    );
  }, []);

  const currentJson = buildFilterJson(conditions);
  const dirty = !!loaded && currentJson !== loaded.filter;

  const apply = () => {
    if (!currentJson) return;
    onApply(currentJson, includeDeleted, viewId);
  };

  const save = async () => {
    const name = saveName.trim();
    if (!name || !currentJson) return;
    await searchService.saveOrderCustomFilter(name, currentJson);
    setLoaded({ name, filter: currentJson });
    setSaveName('');
    refreshSaved();
  };

  const updateExisting = async () => {
    if (!loaded) return;
    await searchService.saveOrderCustomFilter(loaded.name, currentJson);
    setLoaded({ name: loaded.name, filter: currentJson });
    refreshSaved();
  };

  const load = (recno: string) => {
    const f = saved.find((x) => String(x.recno) === recno);
    if (!f) return;
    const parsed = parseFilterJson(f.filter);
    setConditions(parsed.length ? parsed : [{ ...EMPTY }]);
    setIdCounter(parsed.length || 1);
    setLoaded({ name: f.name, filter: canon(f.filter) });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-label">
          Filter Conditions
        </label>
        <div className="flex items-center gap-2">
          {/* Renders nothing until a manager has published a View. Sits beside
              the saved-filter picker because the two are the same kind of
              "start from something someone already set up" control — the
              footer's Save as… pair belongs to saved FILTERS, not Views. */}
          <SearchViewSelect
            views={views}
            myViews={myViews}
            value={viewId}
            onChange={setViewId}
            className="px-2 py-1 text-xs font-normal border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
          />
          {loaded && (
            <span
              title={dirty ? 'Edited — not yet saved' : 'Saved filter'}
              className={`text-xs truncate ${
                dirty
                  ? 'italic text-amber-600 dark:text-amber-400'
                  : 'font-medium text-indigo-700 dark:text-indigo-300'
              }`}
            >
              {loaded.name}
              {dirty ? ' *' : ''}
            </span>
          )}
          {saved.length > 0 && (
            <select
              aria-label="Load saved filter"
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
              defaultValue=""
              onChange={(e) => load(e.target.value)}
            >
              <option value="">Load saved filter…</option>
              {saved.map((f) => (
                <option key={f.recno} value={f.recno}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {conditions.map((condition, i) => (
        <FilterConditionRow
          key={condition.id}
          condition={condition}
          fieldDefinitions={filterableFieldDefs}
          loadingFields={loadingFields}
          onUpdate={updateCondition}
          onRemove={removeCondition}
          removeDisabled={conditions.length === 1}
          autoFocus={condition.id === focusId}
          // Tabbing off the last row's value starts the next condition — but only
          // once that row has a field, so tabbing through a blank row still walks
          // on to Add Filter / Apply instead of stacking up empty rows.
          onTabOut={
            i === conditions.length - 1 && condition.field
              ? () => addCondition(true)
              : undefined
          }
        />
      ))}

      <button
        type="button"
        onClick={() => addCondition()}
        className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Filter
      </button>

      {/* Matches Custom Search: off restricts to deleted='N'; deleted orders that
          do come back are red-highlighted by the results grid. */}
      <label className="flex items-center gap-1.5 text-[0.75rem] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
        <Toggle
          size="sm"
          checked={includeDeleted}
          onChange={setIncludeDeleted}
          ariaLabel="Include deleted orders"
        />
        Include Deleted
      </label>

      <div className="mt-1 flex items-center justify-between gap-2 border-t border-slate-200 dark:border-slate-700 pt-3">
        <div className="flex items-center gap-1.5">
          {loaded && dirty && (
            <button
              type="button"
              onClick={updateExisting}
              title={`Overwrite the saved filter “${loaded.name}”`}
              className="px-2.5 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded"
            >
              Update “{loaded.name}”
            </button>
          )}
          <input
            aria-label="Save filter name"
            className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Save as…"
          />
          <button
            type="button"
            onClick={save}
            disabled={!saveName.trim() || !currentJson}
            className="px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-40"
          >
            Save As
          </button>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!currentJson}
            className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded hover:bg-blue-700 disabled:opacity-40"
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomOrderFilterForm;
